import {
  setPersistence,
  browserLocalPersistence,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signInWithCustomToken as firebaseSignInWithCustomToken,
  signInWithEmailLink,
  isSignInWithEmailLink,
} from 'firebase/auth';
import {
  auth,
  getStepUpAuth,
  STEP_UP_RECAPTCHA_CONTAINER_ID,
  fetchUserInfo as fetchUserInfoFromFirestore,
} from './firebase';

export const signIn = async (email, password) => {
  await setPersistence(auth, browserLocalPersistence);
  await signInWithEmailAndPassword(auth, email, password);
};

export const signInWithGoogle = async () => {
  const googleProvider = new GoogleAuthProvider();
  googleProvider.setCustomParameters({
    prompt: 'select_account',
  });

  const user = await signInWithPopup(auth, googleProvider);

  return user;
};

export const signOut = async () => {
  await firebaseSignOut(auth);
};

// Raw account creation; the backend /users record is provisioned separately
// by GlobalLoginV2.
export const createAuthUser = async (email, password) => createUserWithEmailAndPassword(
  auth,
  email,
  password,
);

export const sendPasswordResetEmail = async (email) => {
  await firebaseSendPasswordResetEmail(auth, email);
};

export const saveCurrentAuthToken = async () => {
  const user = auth.currentUser;

  if (!user) {
    throw new Error('No authenticated user');
  }

  return user.getIdToken();
};

// Single construction point for RecaptchaVerifier. Modular arg order:
// (auth, container, params) — auth first, unlike the v8 (container, params).
const createRecaptchaVerifier = (authInstance, containerId, params) => new RecaptchaVerifier(
  authInstance,
  containerId,
  params,
);

const DEFAULT_RECAPTCHA_CONTAINER_ID = 'recaptcha-container';

// One verifier per auth instance (default app and the step-up app each own a
// container). `widgetId` caches the ID from the first render. Using the cached
// value on reuse avoids calling render() a second time, which can throw
// "already rendered" in some Firebase SDK versions.
const recaptchaStates = new Map();

const createRecaptchaState = (authInstance, containerId) => {
  const state = {
    verifier: createRecaptchaVerifier(authInstance, containerId, { size: 'invisible' }),
    widgetId: null,
  };
  recaptchaStates.set(authInstance, state);

  return state;
};

export const sendPhoneOtp = async (
  phoneNumber,
  { authInstance = auth, containerId = DEFAULT_RECAPTCHA_CONTAINER_ID } = {},
) => {
  let state = recaptchaStates.get(authInstance);

  if (!state) {
    state = createRecaptchaState(authInstance, containerId);
  } else if (window.grecaptcha) {
    if (state.widgetId !== null) {
      // Widget ID already cached — reset directly, no render() call needed.
      window.grecaptcha.reset(state.widgetId);
    } else {
      // grecaptcha available but no cached ID yet — render once to get it.
      state.widgetId = await state.verifier.render();
      window.grecaptcha.reset(state.widgetId);
    }
  } else {
    // grecaptcha not yet available — recreate the verifier to avoid stale state.
    try { state.verifier.clear(); } catch { /* ignore */ }
    state = createRecaptchaState(authInstance, containerId);
  }

  const confirmationResult = await signInWithPhoneNumber(
    authInstance,
    phoneNumber,
    state.verifier,
  );

  // After the first signInWithPhoneNumber the widget is rendered internally.
  // Cache its ID now so subsequent reuse calls never need to call render() at all.
  if (state.widgetId === null && window.grecaptcha) {
    try { state.widgetId = await state.verifier.render(); } catch { /* ignore */ }
  }

  return confirmationResult;
};

export const clearRecaptchaVerifier = (authInstance = auth) => {
  const state = recaptchaStates.get(authInstance);

  if (state) {
    try { state.verifier.clear(); } catch { /* ignore */ }
    recaptchaStates.delete(authInstance);
  }
};

export const confirmPhoneOtp = async (confirmationResult, otpCode) => {
  const result = await confirmationResult.confirm(otpCode);
  const idToken = await result.user.getIdToken();

  return idToken;
};

// Step-up OTP runs on the secondary Firebase app so the email session on the
// default app is never signed out.
export const sendStepUpPhoneOtp = async (phoneNumber) => sendPhoneOtp(phoneNumber, {
  authInstance: getStepUpAuth(),
  containerId: STEP_UP_RECAPTCHA_CONTAINER_ID,
});

export const signOutStepUp = async () => {
  try { await firebaseSignOut(getStepUpAuth()); } catch { /* ignore */ }
};

export const signInWithCustomToken = async (
  customToken,
) => firebaseSignInWithCustomToken(auth, customToken);

export const restoreAuthSession = async (customToken) => {
  await signInWithCustomToken(customToken);
};

export const completeEmailLinkSignIn = (
  email,
  href = window.location.href,
) => signInWithEmailLink(auth, email, href);

export const isEmailSignInLink = (href = window.location.href) => isSignInWithEmailLink(
  auth,
  href,
);

export const getCurrentUser = () => auth.currentUser;

export const fetchUserInfo = async (userId) => fetchUserInfoFromFirestore(userId);
